import React from 'react';
import { Chip, Paper, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslate } from 'react-admin';
import { formatCurrency, formatNumber } from '../../../utils/reportHelpers';
import { KioskTrendChart } from './KioskTrendChart';
import type { DailyReport, OverviewReport, PayoutReport, RefundsReport } from './types';

const SETTLEMENT_COLORS = { ok: 'success', failed: 'error', interrupted: 'warning' } as const;
const percent = (share: number | null) => (share === null ? '-' : `${formatNumber(share * 100, 2)}%`);

const Note = ({ id }: { id: string }) => {
  const translate = useTranslate();
  return <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>{translate(`reports.kiosk.notes.${id}`)}</Typography>;
};

const Empty = () => {
  const translate = useTranslate();
  return <Typography sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>{translate('reports.kiosk.empty')}</Typography>;
};

const ReportTable = ({ columns, numeric, children }: { columns: string[]; numeric: number; children: React.ReactNode }) => {
  const translate = useTranslate();
  return (
    <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((column, index) => (
              <TableCell key={column} align={index >= columns.length - numeric ? 'right' : 'left'} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                {translate(`reports.kiosk.columns.${column}`)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        {children}
      </Table>
    </TableContainer>
  );
};

export const PayoutTable = ({ report }: { report: PayoutReport }) => {
  const translate = useTranslate();
  return (
    <>
      <Note id="payout" />
      {report.rows.length === 0 ? <Empty /> : (
        <ReportTable columns={['site', 'month', 'method', 'sessions', 'energyKwh', 'kept', 'refunded', 'stillOwed']} numeric={5}>
          <TableBody>
            {report.rows.map((row) => (
              <TableRow key={`${row.site_id}-${row.month}-${row.method}`} data-testid={`payout-${row.site_id}-${row.month}-${row.method}`}>
                <TableCell>{row.site_name}</TableCell>
                <TableCell>{row.month}</TableCell>
                <TableCell>{translate(`resources.kiosk-transactions.methods.${row.method}`)}</TableCell>
                <TableCell align="right">{formatNumber(row.sessions)}</TableCell>
                <TableCell align="right">{formatNumber(row.energy_kwh, 3)}</TableCell>
                <TableCell align="right">{formatCurrency(row.kept)}</TableCell>
                <TableCell align="right">{formatCurrency(row.refunded)}</TableCell>
                <TableCell align="right">{formatCurrency(row.still_owed)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow data-testid="payout-total">
              <TableCell colSpan={3} sx={{ fontWeight: 700 }}>{translate('reports.kiosk.total')}</TableCell>
              <TableCell align="right">{formatNumber(report.totals.sessions)}</TableCell>
              <TableCell align="right">{formatNumber(report.totals.energy_kwh, 3)}</TableCell>
              <TableCell align="right">{formatCurrency(report.totals.kept)}</TableCell>
              <TableCell align="right">{formatCurrency(report.totals.refunded)}</TableCell>
              <TableCell align="right">{formatCurrency(report.totals.still_owed)}</TableCell>
            </TableRow>
          </TableFooter>
        </ReportTable>
      )}
    </>
  );
};

export const DailyMatchTable = ({ report }: { report: DailyReport }) => {
  const translate = useTranslate();
  return (
    <>
      <Note id="daily" />
      {report.rows.length === 0 ? <Empty /> : (
        <ReportTable columns={['day', 'method', 'kiosk', 'takenCount', 'taken', 'refundedCount', 'refunded', 'handRefundedCount', 'handRefunded', 'settlement']} numeric={7}>
          <TableBody>
            {report.rows.map((row) => {
              const key = `${row.day}-${row.method}-${row.kiosk_id === null ? 'all' : row.kiosk_id}`;
              return (
                <TableRow key={key} data-testid={`daily-${key}`}>
                  <TableCell>{row.day}</TableCell>
                  <TableCell>{translate(`resources.kiosk-transactions.methods.${row.method}`)}</TableCell>
                  <TableCell>{row.kiosk_name === null ? translate('reports.kiosk.allKiosks') : row.kiosk_name}</TableCell>
                  <TableCell align="right">{formatNumber(row.taken_count)}</TableCell>
                  <TableCell align="right">{formatCurrency(row.taken_total)}</TableCell>
                  <TableCell align="right">{formatNumber(row.refunded_count)}</TableCell>
                  <TableCell align="right">{formatCurrency(row.refunded_total)}</TableCell>
                  <TableCell align="right">{formatNumber(row.hand_refunded_count)}</TableCell>
                  <TableCell align="right">{formatCurrency(row.hand_refunded_total)}</TableCell>
                  <TableCell align="right">
                    {row.settlement_outcome !== null
                      ? <Chip size="small" color={SETTLEMENT_COLORS[row.settlement_outcome]} label={translate(`reports.kiosk.settlement.${row.settlement_outcome}`)} />
                      : row.method === 'octopus' ? translate('reports.kiosk.settlement.none') : '-'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </ReportTable>
      )}
    </>
  );
};

export const RefundsHealthTable = ({ report }: { report: RefundsReport }) => {
  const translate = useTranslate();
  return (
    <>
      <Note id="refunds" />
      <ReportTable columns={['outcome', 'count', 'amount', 'avgHours']} numeric={3}>
        <TableBody>
          {report.rows.map((row) => (
            <TableRow key={row.outcome} data-testid={`refunds-${row.outcome}`}>
              <TableCell>{translate(`reports.kiosk.outcomes.${row.outcome}`)}</TableCell>
              <TableCell align="right">{formatNumber(row.count)}</TableCell>
              <TableCell align="right">{formatCurrency(row.amount)}</TableCell>
              <TableCell align="right">{row.avg_hours_to_refund === null ? '-' : formatNumber(row.avg_hours_to_refund, 2)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </ReportTable>
    </>
  );
};

export const OverviewTable = ({ report }: { report: OverviewReport }) => (
  <>
    <Note id="overview" />
    {report.rows.length === 0 ? <Empty /> : (
      <>
        <KioskTrendChart trend={report.trend} />
        <ReportTable
          columns={['site', 'month', 'kioskSessions', 'kioskEnergy', 'kioskKept', 'appSessions', 'appEnergy', 'appRevenue', 'kioskSessionShare', 'kioskRevenueShare']}
          numeric={8}
        >
          <TableBody>
            {report.rows.map((row) => (
              <TableRow key={`${row.site_id}-${row.month}`} data-testid={`overview-${row.site_id}-${row.month}`}>
                <TableCell>{row.site_name}</TableCell>
                <TableCell>{row.month}</TableCell>
                <TableCell align="right">{formatNumber(row.kiosk_sessions)}</TableCell>
                <TableCell align="right">{formatNumber(row.kiosk_energy_kwh, 3)}</TableCell>
                <TableCell align="right">{formatCurrency(row.kiosk_kept)}</TableCell>
                <TableCell align="right">{formatNumber(row.app_sessions)}</TableCell>
                <TableCell align="right">{formatNumber(row.app_energy_kwh, 3)}</TableCell>
                <TableCell align="right">{formatCurrency(row.app_revenue)}</TableCell>
                <TableCell align="right">{percent(row.kiosk_session_share)}</TableCell>
                <TableCell align="right">{percent(row.kiosk_revenue_share)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </ReportTable>
      </>
    )}
  </>
);
